import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';
import { removeToken } from '../utils/auth';
import ChangePasswordModal from './ChangePasswordModal';
import { apiEndpoints } from '../services/api';

const NAV_ITEMS = [
  { path: '/',           label: 'Dashboard',  icon: 'M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z' },
  {
    path: '/vehicles', label: 'Permanent Vehicles', icon: null, vehicleIcon: true,
    children: [{ path: '/vehicles/temporary', label: 'Temporary Vehicles', vehicleIcon: true }],
  },
  { path: '/drivers',    label: 'Drivers',    icon: 'M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z' },
  { path: '/log',        label: 'Log',        icon: 'M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v5a1 1 0 00.4.8l3 2a1 1 0 101.2-1.6L11 9.5V5z', adminOnly: true },
  { path: '/documents',  label: 'Documents',  icon: 'M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z' },
  { path: '/costs',      label: 'Costs',      icon: 'M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z' },
  { path: '/events',     label: 'Events',     icon: 'M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z' },
  { path: '/accidents',  label: 'Accidents',  icon: 'M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z' },
  { path: '/leasing',    label: 'Leasing',    icon: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z' },
  { path: '/companies',  label: 'Companies',  icon: 'M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z' },
  { path: '/alerts',     label: 'Alerts',     icon: 'M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z' },
  { path: '/suppliers',  label: 'Providers',  icon: 'M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z' },
];

const TopBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme, isAdmin, bumpPageReset } = useAppContext();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [openMenu, setOpenMenu] = useState(
    () => NAV_ITEMS.find((item) => item.children?.some((c) => c.path === location.pathname))?.path || null
  );

  const getPageTitle = () => {
    for (const n of NAV_ITEMS) {
      if (n.path === location.pathname) return n.label;
      const child = n.children?.find((c) => c.path === location.pathname);
      if (child) return child.label;
    }
    if (location.pathname.startsWith('/suppliers')) return 'Providers';
    return 'Fleet OS';
  };

  // Keep the parent expanded whenever you're actually on one of its child
  // pages, so the submenu never hides the page you're currently viewing.
  useEffect(() => {
    const parent = NAV_ITEMS.find((item) => item.children?.some((c) => c.path === location.pathname));
    if (parent) setOpenMenu(parent.path);
  }, [location.pathname]);

  const toggleMenu = (path, e) => {
    e.stopPropagation();
    setOpenMenu((prev) => (prev === path ? null : path));
  };

  // Tapping the link for the page you're already on doesn't change the URL,
  // so React Router won't reset it — force it back to its default view instead.
  const handleNav = (path) => {
    if (location.pathname === path) bumpPageReset();
    else navigate(path);
    setMenuOpen(false);
  };

  const handleLogout = () => {
    apiEndpoints.auth.logout().catch(() => {}); // best-effort audit log entry, don't block logout on it
    removeToken();
    setMenuOpen(false);
    navigate('/login');
  };

  return (
    <>
      <div className="topbar">
        <div className="topbar-logo" style={{ cursor: 'pointer' }} onClick={() => handleNav('/')}>
          <img src="/logo-white-fleet.png" alt="Fleet OS" style={{ height: 36, width: 110, objectFit: 'contain' }} />
        </div>

        <div className="topbar-page-title">{getPageTitle()}</div>

        <div className="topbar-actions">
          <button className="topbar-btn" onClick={toggleTheme}>
            {theme === 'light' ? (
              <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" />
              </svg>
            )}
          </button>

          <button className="topbar-btn" onClick={() => setMenuOpen(true)} style={{ flexDirection: 'column', gap: '4px' }}>
            <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>

      {/* Full-screen nav drawer */}
      {menuOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 500,
          background: 'rgba(0,10,20,0.7)', backdropFilter: 'blur(4px)',
        }} onClick={() => setMenuOpen(false)}>
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: 'linear-gradient(180deg, #001e33 0%, #002f4e 100%)',
            borderRadius: '20px 20px 0 0',
            padding: '8px 0 calc(env(safe-area-inset-bottom, 0px) + 16px)',
            maxHeight: '85dvh', overflowY: 'auto',
            boxShadow: '0 -8px 40px rgba(0,0,0,0.4)',
          }} onClick={(e) => e.stopPropagation()}>
            {/* Handle bar + close button row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', margin: '8px 0 4px' }}>
              <div style={{ width: 40, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.2)' }} />
              <button
                onClick={() => setMenuOpen(false)}
                style={{
                  position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                  background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%',
                  width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: 'rgba(255,255,255,0.7)',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            <div style={{ padding: '8px 12px 8px', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'rgba(255,255,255,0.35)' }}>
              Navigation
            </div>

            {NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin).map((item) => {
              const active = location.pathname === item.path;
              const hasChildren = !!item.children?.length;
              const expanded = hasChildren && openMenu === item.path;
              return (
                <React.Fragment key={item.path}>
                  <div
                    onClick={() => handleNav(item.path)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '14px',
                      padding: '13px 20px', cursor: 'pointer',
                      background: active ? 'rgba(0,119,204,0.22)' : 'transparent',
                      borderLeft: active ? '3px solid #0ea5e9' : '3px solid transparent',
                      margin: '2px 8px', borderRadius: '10px',
                      transition: 'background 0.15s',
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill={active ? '#38bdf8' : 'rgba(255,255,255,0.55)'}>
                      {item.vehicleIcon ? (
                        <path d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      ) : (
                        <path fillRule="evenodd" d={item.icon} clipRule="evenodd" />
                      )}
                    </svg>
                    <span style={{ fontSize: '15px', fontWeight: active ? 700 : 500, color: active ? '#fff' : 'rgba(255,255,255,0.7)' }}>
                      {item.label}
                    </span>
                    {active && <div style={{ marginLeft: hasChildren ? 0 : 'auto', width: 6, height: 6, borderRadius: '50%', background: '#38bdf8' }} />}
                    {hasChildren && (
                      <span
                        role="button"
                        aria-label={expanded ? 'Collapse' : 'Expand'}
                        onClick={(e) => toggleMenu(item.path, e)}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          width: 28, height: 28, marginLeft: 'auto', flexShrink: 0,
                          transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.15s',
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="rgba(255,255,255,0.6)">
                          <path fillRule="evenodd" d="M7.293 4.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414-1.414L11.586 10 7.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </span>
                    )}
                  </div>
                  {hasChildren && expanded && item.children.map((child) => {
                    const childActive = location.pathname === child.path;
                    return (
                      <div
                        key={child.path}
                        onClick={() => handleNav(child.path)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '14px',
                          padding: '11px 20px 11px 40px', cursor: 'pointer',
                          background: childActive ? 'rgba(0,119,204,0.22)' : 'transparent',
                          borderLeft: childActive ? '3px solid #0ea5e9' : '3px solid transparent',
                          margin: '2px 8px', borderRadius: '10px',
                          transition: 'background 0.15s',
                        }}
                      >
                        <span style={{ fontSize: '14px', fontWeight: childActive ? 700 : 500, color: childActive ? '#fff' : 'rgba(255,255,255,0.65)' }}>
                          {child.label}
                        </span>
                        {childActive && <div style={{ marginLeft: 'auto', width: 6, height: 6, borderRadius: '50%', background: '#38bdf8' }} />}
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}

            <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '12px 20px' }} />

            {isAdmin && (
            <div
              onClick={() => { setMenuOpen(false); setShowChangePw(true); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '14px',
                padding: '13px 20px', cursor: 'pointer',
                margin: '2px 8px', borderRadius: '10px',
                borderLeft: '3px solid transparent',
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0110 0v4"/>
              </svg>
              <span style={{ fontSize: '15px', fontWeight: 500, color: 'rgba(255,255,255,0.7)' }}>Change Password</span>
            </div>
            )}

            <div
              onClick={handleLogout}
              style={{
                display: 'flex', alignItems: 'center', gap: '14px',
                padding: '13px 20px', cursor: 'pointer',
                margin: '2px 8px', borderRadius: '10px',
                borderLeft: '3px solid transparent',
              }}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="rgba(239,68,68,0.8)">
                <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
              </svg>
              <span style={{ fontSize: '15px', fontWeight: 500, color: '#ef4444' }}>Logout</span>
            </div>
          </div>
        </div>
      )}

      <ChangePasswordModal isOpen={showChangePw} onClose={() => setShowChangePw(false)} />
    </>
  );
};

export default TopBar;
